import { createFilterOptions } from "@mui/material";
import User from "assets/img/svg/user";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import { useTranslation } from "react-i18next";
import type { TImportDataSaleMetadata } from "store/dataImportSlice";
import type { InvoicePercentage } from "store/invoicePercentages";
import type { DTOBexioGETContact, DTOBexioGetInvoice, TCalculationMethodRender } from "types";

export type ContactRender = {
    name: string;
    id: number;
};

/**
 * Render a contact option for the autocomplete form of the bexio contact.
 * @param props {Object} - The props.
 * @param option {Object} - The option.
 */
const renderContactOption = (props: any, option: ContactRender) => (
    <li {...props}>
        <User />
        <span>{option.name}</span>
    </li>
);

const renderCalculationMethod = (props, options: { id: string; name: string }) => (
    <li {...props}>
        <span>{options.name}</span>
    </li>
);

const filterContactOptions = createFilterOptions({
    matchFrom: "start",
    stringify: (option: any) => option.name,
});

/**
 * Little component to display the modal content for sending invoices.
 *
 * @param params {Object} - The parameters.
 * @param params.afterImportInfo {TImportDataSaleMetadata} - The metadata after the import.
 * @param params.bexioContacts {DTOBexioGETContact[]} - The Bexio contacts.
 * @param params.setSelectedBexioContact {Function} - The function to set the selected Bexio contact.
 * @param params.invoicePercentages {Array} - Array with invoice percentages saved from settings.
 *
 * @constructor
 */
export const CreateInvoiceModalContent = ({
    afterImportInfo,
    bexioContacts,
    calculationMethods,
    setSelectedBexioContact,
    setTurnoverCalculationChoice,
    nbOfImportedLines,
    createdInvoice,
    failureReasonOnCreateInvoice,
    invoicePercentages,
}: {
    afterImportInfo: TImportDataSaleMetadata;
    bexioContacts: Array<DTOBexioGETContact>;
    calculationMethods: Array<TCalculationMethodRender>;

    setSelectedBexioContact: (value: DTOBexioGETContact) => void;
    setTurnoverCalculationChoice: (value: "crealo" | "file") => void;
    nbOfImportedLines?: number;

    createdInvoice: Partial<DTOBexioGetInvoice>;
    failureReasonOnCreateInvoice: string;
    invoicePercentages: InvoicePercentage[];
}) => {
    const [t] = useTranslation();

    const mappedContacts = bexioContacts.map((contact) => {
        return {
            name: contact.name_1 + (contact.name_2 ? ` (${contact.name_2})` : ""),
            id: contact.id,
        } as ContactRender;
    });

    return (
        <div className={"import-modal-finish center"}>
            <p className={`invoice`}>
                {!failureReasonOnCreateInvoice?.length && (
                    <span className={"confirmed"}>
                        {t(
                            afterImportInfo!.invoiceable || (invoicePercentages && invoicePercentages.length > 0)
                                ? "ImportData.SuccessfullyImportedParagraphWithInvoice1"
                                : "ImportData.SuccessfullyImportedParagraphNoInvoice",
                        ).replace("{{LINES_IMPORTED}}", nbOfImportedLines?.toString() || "x")}
                    </span>
                )}

                {/*{!!afterImportInfo!.invoiceable && (*/}
                {/*    <>*/}
                {/*        <br />*/}
                {/*        {t("ImportData.SuccessfullyImportedParagraphWithInvoice2").replace(*/}
                {/*            "{{AMOUNT}}",*/}
                {/*            formatNumber(afterImportInfo!.invoiceable / 100, {*/}
                {/*                always2Digit: true,*/}
                {/*                isCurrency: true,*/}
                {/*            }),*/}
                {/*        )}*/}
                {/*    </>*/}
                {/*)}*/}
            </p>

            {/**
             * "!!" is used to convert the value to a boolean, else, we would have "0" as HTML.
             * This is in case they want to send the invoice from Crealo.
             * */}
            {!!afterImportInfo!.invoiceable && !failureReasonOnCreateInvoice && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <AutocompleteFormComponent
                        label={t("ImportData.ContactSelectLabel")}
                        placeholder={t("ImportData.ContactSelectPlaceholder")}
                        options={mappedContacts}
                        renderOptionText={(option: ContactRender) => option.name}
                        renderOption={renderContactOption}
                        filterOptions={filterContactOptions}
                        onChange={(e, value) => {
                            setSelectedBexioContact(value);
                        }}
                        sx={{ maxWidth: "350px", marginBottom: "1rem" }}
                    />
                    {/* <AutocompleteFormComponent
                        label={t("ImportData.TurnoverCalculationChoice")}
                        placeholder={t("ImportData.CalculationMethod")}
                        options={calculationMethods}
                        renderOption={renderCalculationMethod}
                        renderOptionText={(option) => option.name}
                        onChange={(e, value) => {
                            setTurnoverCalculationChoice(value.id ?? -1);
                        }}
                        sx={{ maxWidth: "350px" }}
                        disableClearable
                    /> */}
                </div>
            )}

            {!!failureReasonOnCreateInvoice && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "14px" }}>{t("Invoice.Error")}</span>
                    <span style={{ fontSize: "14px", fontWeight: "normal" }}>{failureReasonOnCreateInvoice ?? ""}</span>
                </div>
            )}
        </div>
    );
};
